import clsx from 'clsx';
import React, { useState } from 'react';
import { Skeleton } from './Feedback';
import { HUE_FILL, type Hue } from './hue';

/**
 * Breakdown: rows of `label · sub · value · percent` over a thin track —
 * the V2 answer to a donut or a stack of glowing bars. Sorted descending,
 * readable as a list, every percentage visible without hovering. Collapses
 * after `max` rows behind "Show n more".
 *
 * `palette="mono"` (default) paints every bar in the data hue.
 * `palette="multi"` gives each row a categorical hue — by rank, or pinned
 * with `tone` so a category keeps its colour across pages — and repeats it
 * in a dot before the label, so colour reads as a legend, never alone.
 *
 * Percent is computed from `total` when given (so rounding is consistent
 * across rows), otherwise taken from the row's own `percent`.
 *
 * `size="lg"` is the chart form for a dashboard section (see the prop).
 */

export type BreakdownTone = Hue | 'success' | 'warning' | 'danger' | 'neutral';

/** Rank-based cycle for `multi` rows without a pinned tone. */
const PALETTE: BreakdownTone[] = [
  'accent',
  'teal',
  'violet',
  'amber',
  'rose',
  'sky',
  'lime',
];

const FILL: Record<BreakdownTone | 'data', string> = {
  ...HUE_FILL,
  data: 'bg-v2-data',
  success: 'bg-v2-success',
  warning: 'bg-v2-warning',
  danger: 'bg-v2-danger',
  neutral: 'bg-v2-fg-4',
};

export interface BreakdownRow {
  key: string;
  label: React.ReactNode;
  value: number;
  /** 0–100. Used when the list has no `total`. */
  percent?: number;
  /** Quiet sub-label after the label ("Most often: new reservation"). */
  sub?: React.ReactNode;
  /** Pin a colour to this row (`multi` palette only). */
  tone?: BreakdownTone;
}

export const Breakdown: React.FC<{
  rows: BreakdownRow[];
  total?: number;
  /** Rows shown before the "Show more" toggle. Default 6. */
  max?: number;
  /** Noun for the value's accessible label ("calls"). */
  noun?: string;
  palette?: 'mono' | 'multi';
  /**
   * `md` (default) is the dense list for panels and rails. `lg` is the
   * chart form for a dashboard section: 14px labels, an 8px track, a
   * square category swatch instead of a dot, and the sub-label on its own
   * line below `sm` so the row never wraps mid-sentence.
   */
  size?: 'md' | 'lg';
  className?: string;
  'aria-label'?: string;
}> = ({
  rows,
  total,
  max = 6,
  noun,
  palette = 'mono',
  size = 'md',
  className,
  ...rest
}) => {
  const lg = size === 'lg';
  const [expanded, setExpanded] = useState(false);
  const sorted = [...rows].sort((a, b) => b.value - a.value);
  const visible = expanded ? sorted : sorted.slice(0, max);
  const hidden = sorted.length - visible.length;

  const pct = (r: BreakdownRow) => {
    const p = total && total > 0 ? (r.value / total) * 100 : (r.percent ?? 0);
    return Math.min(100, Math.max(0, p));
  };
  const fillFor = (r: BreakdownRow, rank: number) => {
    if (palette === 'mono') return FILL.data;
    // Past the palette, neutral rather than repeating a colour that already
    // means something above.
    return FILL[r.tone ?? PALETTE[rank] ?? 'neutral'];
  };

  return (
    <div className={className}>
      <ol aria-label={rest['aria-label']} className="flex flex-col">
        {visible.map((r, rank) => {
          const p = pct(r);
          const fill = fillFor(r, rank);
          return (
            <li
              key={r.key}
              className={clsx(
                'border-b border-v2-line/80 last:border-b-0',
                lg ? 'py-3' : 'py-2.5',
              )}
            >
              <div className="flex items-baseline gap-4">
                <div
                  className={clsx(
                    'flex min-w-0 flex-1 items-baseline gap-2',
                    lg && 'flex-wrap gap-y-0 sm:flex-nowrap',
                  )}
                >
                  {palette === 'multi' && (
                    <span
                      aria-hidden
                      className={clsx(
                        'relative top-px shrink-0 self-center',
                        lg
                          ? 'h-2.5 w-2.5 rounded-[3px]'
                          : 'h-2 w-2 rounded-full',
                        fill,
                      )}
                    />
                  )}
                  <span
                    className={clsx(
                      'shrink-0 font-medium text-v2-fg',
                      lg ? 'text-v2-14' : 'text-v2-13',
                    )}
                  >
                    {r.label}
                  </span>
                  {r.sub && (
                    <span
                      className={clsx(
                        'min-w-0 truncate text-v2-12 text-v2-fg-3',
                        lg && 'basis-full pl-[18px] sm:basis-auto sm:pl-0',
                      )}
                    >
                      <span className={clsx(lg && 'hidden sm:inline')}>· </span>
                      {r.sub}
                    </span>
                  )}
                </div>
                <span
                  className={clsx(
                    'v2-num w-12 shrink-0 text-right text-v2-fg',
                    lg ? 'text-v2-14 font-medium' : 'text-v2-13',
                  )}
                  aria-label={
                    noun ? `${r.value.toLocaleString()} ${noun}` : undefined
                  }
                >
                  {r.value.toLocaleString()}
                </span>
                <span className="v2-num w-10 shrink-0 text-right text-v2-12 text-v2-fg-3">
                  {Math.round(p)}%
                </span>
              </div>
              <div
                className={clsx(
                  'flex items-center gap-4',
                  lg ? 'mt-2' : 'mt-1.5',
                )}
              >
                <span
                  aria-hidden
                  className={clsx(
                    'min-w-0 flex-1 overflow-hidden rounded-full bg-v2-line',
                    lg ? 'h-2' : 'h-1.5',
                  )}
                >
                  <span
                    className={clsx('block h-full rounded-full', fill)}
                    style={{ width: `${p > 0 ? Math.max(1.5, p) : 0}%` }}
                  />
                </span>
                {/* Reserve the value and percent columns so tracks align. */}
                <span aria-hidden className="w-12 shrink-0" />
                <span aria-hidden className="w-10 shrink-0" />
              </div>
            </li>
          );
        })}
      </ol>
      {sorted.length > max && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-2 inline-flex h-7 items-center rounded-v2 px-1.5 text-v2-12 text-v2-fg-3 hover:bg-v2-sunken hover:text-v2-fg"
        >
          {expanded ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  );
};

export const BreakdownSkeleton: React.FC<{ rows?: number }> = ({
  rows = 4,
}) => (
  <ol className="flex flex-col" aria-hidden>
    {Array.from({ length: rows }).map((_, i) => (
      <li key={i} className="border-b border-v2-line/80 py-2.5 last:border-b-0">
        <div className="flex items-baseline gap-4">
          <Skeleton width={`${34 - i * 6}%`} className="h-3" />
          <span className="ml-auto" />
          <Skeleton width={36} className="h-3" />
          <Skeleton width={28} className="h-3" />
        </div>
        <div className="mt-2 flex items-center gap-4">
          <Skeleton className="h-1.5 flex-1" width={`${78 - i * 18}%`} />
          <span className="w-12 shrink-0" />
          <span className="w-10 shrink-0" />
        </div>
      </li>
    ))}
  </ol>
);
